import cx from 'classnames';
import { Asset } from '../AtomicComponents/Asset';
import { IComparisonListFields } from '@/types/contentful';

import { FeatureDescriptions } from './FeatureDescriptions';
import { Asset as ContentfulAsset } from 'contentful';
import { themeColors } from './theme';
import RichText from '../RichText';
import { BLOCKS } from '@contentful/rich-text-types';

interface IEyebrowProps
  extends Pick<
    IComparisonListFields,
    'eyebrowIcon' | 'eyebrowLogo' | 'eyebrowText'
  > {
  textColor?: string;
}

function Eyebrow({
  eyebrowIcon,
  eyebrowLogo,
  eyebrowText,
  textColor
}: IEyebrowProps) {
  if (eyebrowLogo) {
    return (
      <div className="mb-4 flex flex-col items-center justify-center">
        <div className="flex justify-center">
          <div className="h-6">
            <Asset
              asset={eyebrowLogo as ContentfulAsset}
              className="h-full max-h-full"
              alt="Comparison List Logo"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    );
  }

  if (eyebrowIcon?.fields?.file?.url || eyebrowText) {
    return (
      <div className="mb-4 flex items-center justify-center space-x-1">
        {eyebrowIcon?.fields?.file?.url && (
          <Asset
            asset={eyebrowIcon as ContentfulAsset}
            className="inline-block"
            loading="lazy"
          />
        )}
        <h2 className={cx('!typography-display-5 inline-block', textColor)}>
          {eyebrowText}
        </h2>
      </div>
    );
  }

  return null;
}

export function ComparisonList({
  header,
  paragraph,
  eyebrowIcon,
  eyebrowLogo,
  eyebrowText,
  backgroundColor,
  cardsColor,
  featureDescriptions,
  theme
}: IComparisonListFields) {
  const hasTheme = Boolean(theme);
  const themeKey = theme as keyof typeof themeColors;
  const themeColor = hasTheme ? themeColors[themeKey] : undefined;

  const hasBackground = Boolean(backgroundColor) || Boolean(themeColor);

  return (
    <div className={cx('px-4 tablet:px-6')}>
      <div
        className={cx(
          'py-10 desktop:py-25',
          hasBackground && 'rounded-md',
          hasTheme && themeColor?.background
        )}
        style={{
          backgroundColor: Boolean(backgroundColor)
            ? backgroundColor
            : undefined
        }}
      >
        <div className="px-4 pb-4 text-center tablet:px-6">
          <Eyebrow
            eyebrowIcon={eyebrowIcon}
            eyebrowLogo={eyebrowLogo}
            eyebrowText={eyebrowText}
            textColor={hasTheme ? themeColor?.text : undefined}
          />
          <h1
            className={cx(
              '!typography-display-2 mb-4',
              hasTheme && themeColor?.text
            )}
          >
            {header}
          </h1>
          {paragraph && (
            <div className="mx-auto mb-4 max-w-[820px]">
              <RichText
                data={paragraph}
                overrideOptions={{
                  [BLOCKS.PARAGRAPH]: (_node, children) => (
                    <p
                      className={cx(
                        '!typography-display-4 leading-6 tablet:leading-7 desktop:leading-8',
                        hasTheme && themeColor?.text
                      )}
                    >
                      {children}
                    </p>
                  ),
                  [BLOCKS.HEADING_4]: (_node, children) => (
                    <p
                      className={cx(
                        '!typography-display-4',
                        hasTheme && themeColor?.text
                      )}
                    >
                      {children}
                    </p>
                  ),
                  [BLOCKS.HEADING_5]: (_node, children) => (
                    <p
                      className={cx(
                        '!typography-display-5',
                        hasTheme && themeColor?.text
                      )}
                    >
                      {children}
                    </p>
                  )
                }}
              />
            </div>
          )}
        </div>
        <FeatureDescriptions
          featureDescriptions={featureDescriptions}
          cardsColors={cardsColor}
        />
      </div>
    </div>
  );
}
